import {
  Button,
  FormControl,
  FormikAutocomplete,
  FormikTextField,
  FullScreenModal,
  Icon,
  IconButton,
  toasterService
} from '@hevoio/hevo-ui-toolkit';
import { Field, Formik, FormikProps } from 'formik';
import { useRef, useState } from 'react';
import styles from './CreateVPCModal.module.scss';
import { AWS_REGIONS, VPC_PEERING_INITIATE_DOC_LINK } from './constants';
import {
  buildEditPayload,
  getInitialValues,
  getValidationSchema,
  isFieldEditable,
  parseCIDRList,
  VPCPeeringFormField,
  VPCPeeringFormValues
} from './configHelper';
import ErrorFocus from '../../../../../form/components/error-focus/ErrorFocus';
import useUserService from '../../../../../auth/hooks/useUserService';
import useService from '../../../../../injector/hooks/useService';
import { MultiRegionService } from '../../../../../auth/services/multi-region.service';
import TeamAPI from '../../../api/TeamAPI';
import { getErrorMessageFromObj } from '../../../../../core/utils/request';
import { SetupGuideDocs } from '../../../../../docs/components/setup-guide-docs/SetupGuideDocs';
import { VPC_PEERING_FORM_DATA_IDS } from '../../dataIds';
import { CIDREditMode, VPCPeeringConnection, VPCPeeringFormMode } from '../../interface';

const TITLE: Record<VPCPeeringFormMode, string> = {
  [VPCPeeringFormMode.CREATE]: 'Create VPC Peering Connection',
  [VPCPeeringFormMode.EDIT]: 'Edit VPC Peering Connection',
  [VPCPeeringFormMode.RESUBMIT]: 'Edit VPC Peering Connection'
};

const SUCCESS_FALLBACK: Record<VPCPeeringFormMode, string> = {
  [VPCPeeringFormMode.CREATE]: 'Peering request created',
  [VPCPeeringFormMode.EDIT]: 'Peering connection updated',
  [VPCPeeringFormMode.RESUBMIT]: 'Peering request resubmitted'
};

const getSubmitLabel = (mode: VPCPeeringFormMode, hasEditableFields: boolean): string => {
  if (mode === VPCPeeringFormMode.CREATE) {
    return 'Create';
  }

  // A resubmit with nothing editable is a plain retry; with corrections it is
  // the save and the retry in one call.
  if (mode === VPCPeeringFormMode.RESUBMIT) {
    return hasEditableFields ? 'Save & Retry' : 'Retry';
  }

  return 'Save';
};

export interface VPCPeeringModalProps {
  open: boolean;
  mode?: VPCPeeringFormMode;
  connection?: VPCPeeringConnection | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export function VPCPeeringModal({
  open,
  mode = VPCPeeringFormMode.CREATE,
  connection,
  onClose,
  onSuccess
}: VPCPeeringModalProps) {
  const formikRef = useRef<FormikProps<VPCPeeringFormValues>>(null);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useUserService();
  const multiRegionService = useService(MultiRegionService);

  // clusterId is the canonical region identifier (see user.service.ts); the
  // multi-region selection wins for multi-region users when one is set.
  const region =
    (multiRegionService.isMultiRegionUser()
      ? multiRegionService.getSelectedRegionValue()?.id
      : undefined) ?? user.clusterId;

  const isCreate = mode === VPCPeeringFormMode.CREATE;
  const isEditable = (field: VPCPeeringFormField) => isFieldEditable(field, mode, connection);
  const hasEditableFields = [
    VPCPeeringFormField.NAME,
    VPCPeeringFormField.CIDR_RANGE,
    VPCPeeringFormField.AWS_REGION
  ].some(isEditable);

  const title = TITLE[mode];
  const submitLabel = getSubmitLabel(mode, hasEditableFields);

  const sendRequest = () => {
    const { values } = formikRef.current;

    if (isCreate) {
      const { cidr_range: cidrRange, ...rest } = values;
      return TeamAPI.createPeeringRequest({
        ...rest,
        cidr: parseCIDRList(cidrRange),
        team_name: user.teamName,
        hevo_region: region,
        team_id: user.teamId
      });
    }

    const payload = buildEditPayload(values, mode, connection);

    if (mode === VPCPeeringFormMode.RESUBMIT) {
      return TeamAPI.resubmitPeeringRequest(connection.id, payload);
    }

    // An empty PATCH body is a 400. Say so here rather than round-tripping.
    if (!Object.keys(payload).length) {
      return Promise.reject(new Error('Nothing to save — no editable field was changed.'));
    }

    return TeamAPI.editPeeringRequest(connection.id, payload);
  };

  const onSubmit = () => {
    setSubmitting(true);

    return sendRequest()
      .then((res: { message?: string }) => {
        toasterService.pop('success', res?.message ?? SUCCESS_FALLBACK[mode]);
        onSuccess?.();
        onClose();
      })
      .catch(error => {
        toasterService.pop('error', getErrorMessageFromObj(error));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <FullScreenModal open={open} onClose={onClose}>
      <div className='d-flex flex-col h-100 w-100'>
        <div className='d-flex justify-between align-items-center p-4 border-bottom w-100'>
          <p className='m-0 text-label-small text-strong-950'>{title}</p>
          <IconButton
            dataId={VPC_PEERING_FORM_DATA_IDS.closeModal}
            label=''
            onClick={onClose}
          >
            <Icon name='close' />
          </IconButton>
        </div>

        <div className='d-flex flex-1 mw-0 overflow-hidden'>
          <div className='flex-1 mw-0 p-6 flex-col gap-4 overflow-auto'>
            {!isCreate && (connection?.failure_hint || connection?.message) && (
              <div className={styles.noticeBanner} data-id={VPC_PEERING_FORM_DATA_IDS.failureHint}>
                <Icon name='error-outline' size={1} className={styles.noticeBannerIcon} />
                <div>
                  {connection?.failure_hint && <p className='m-0'>{connection.failure_hint}</p>}
                  {connection?.message && (
                    <p className={styles.noticeBannerDetail}>{connection.message}</p>
                  )}
                </div>
              </div>
            )}

            {!isCreate && connection?.cidr_edit_mode === CIDREditMode.ADDITIVE_ONLY && (
              <div className={styles.noticeBanner} data-id={VPC_PEERING_FORM_DATA_IDS.additiveNote}>
                <Icon name='error-outline' size={1} className={styles.noticeBannerIcon} />
                <p className='m-0'>
                  This peering is live. CIDRs can be added but not removed — keep the existing
                  entries and append to the list.
                </p>
              </div>
            )}

            <Formik
              initialValues={getInitialValues(connection)}
              validationSchema={getValidationSchema(mode, connection)}
              onSubmit={onSubmit}
              innerRef={formikRef}
              enableReinitialize
            >
              {props => (
                <div className={styles.formFields}>
                  <ErrorFocus formik={props} />

                  <FormControl>
                    <Field
                      name={VPCPeeringFormField.NAME}
                      dataId={VPC_PEERING_FORM_DATA_IDS.name}
                      label='Connection Name'
                      placeholder='Connection Name'
                      type='string'
                      disableDecimal
                      required
                      inlineError
                      disabled={!isEditable(VPCPeeringFormField.NAME)}
                      component={FormikTextField}
                      helperText='A unique name for this VPC Peering connection'
                    />
                  </FormControl>

                  <FormControl>
                    <Field
                      name={VPCPeeringFormField.VPC_ID}
                      dataId={VPC_PEERING_FORM_DATA_IDS.vpcId}
                      label='VPC ID'
                      placeholder='VPC ID'
                      type='string'
                      disableDecimal
                      required
                      inlineError
                      disabled={!isEditable(VPCPeeringFormField.VPC_ID)}
                      component={FormikTextField}
                      helperText={
                        isCreate
                          ? 'The ID of the AWS VPC where your Source or Destination database is located.'
                          : 'The VPC cannot be changed — a different VPC is a different peering. Delete this request and create a new one.'
                      }
                    />
                  </FormControl>

                  <FormControl>
                    <Field
                      name={VPCPeeringFormField.CIDR_RANGE}
                      dataId={VPC_PEERING_FORM_DATA_IDS.cidrRange}
                      label='CIDR Range'
                      placeholder='CIDR Range'
                      type='string'
                      disableDecimal
                      required
                      inlineError
                      disabled={!isEditable(VPCPeeringFormField.CIDR_RANGE)}
                      component={FormikTextField}
                      helperText='The IP address range of your AWS VPC in CIDR format. For example, 172.31.0.0/16'
                    />
                  </FormControl>

                  <FormControl>
                    <Field
                      name={VPCPeeringFormField.AWS_ACCOUNT_ID}
                      dataId={VPC_PEERING_FORM_DATA_IDS.awsAccountId}
                      label='AWS Account ID'
                      placeholder='AWS Account ID'
                      type='string'
                      disableDecimal
                      required
                      inlineError
                      disabled={!isEditable(VPCPeeringFormField.AWS_ACCOUNT_ID)}
                      component={FormikTextField}
                      helperText={
                        isCreate
                          ? 'The 12-digit ID of the AWS account that contains the VPC you want to peer.'
                          : 'The AWS account cannot be changed on an existing request.'
                      }
                    />
                  </FormControl>

                  <FormControl>
                    <Field
                      name={VPCPeeringFormField.AWS_REGION}
                      dataId={VPC_PEERING_FORM_DATA_IDS.awsRegion}
                      label='AWS Region'
                      placeholder='AWS Region'
                      type='string'
                      disableDecimal
                      required
                      inlineError
                      disabled={!isEditable(VPCPeeringFormField.AWS_REGION)}
                      component={FormikAutocomplete}
                      textFieldProps={{
                        label: 'AWS Region',
                        required: true,
                        disabled: !isEditable(VPCPeeringFormField.AWS_REGION),
                        helperText:
                          'The AWS region where your Source or Destination database is hosted.'
                      }}
                      options={AWS_REGIONS}
                    />
                  </FormControl>
                </div>
              )}
            </Formik>
          </div>

          <SetupGuideDocs
            className={styles.setupGuideDocs}
            docLink={VPC_PEERING_INITIATE_DOC_LINK}
            autoScrollToSectionOnLoad
            scrollToSectionProps={{ sourceSection: 'Create VPC Peering' }}
          />
        </div>

        <div className='border-top p-2 w-100 d-flex justify-center'>
          <Button
            variation='outline'
            dataId={VPC_PEERING_FORM_DATA_IDS.cancel}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className='ml-2'
            dataId={VPC_PEERING_FORM_DATA_IDS.submit}
            disabled={submitting}
            showProgress={submitting}
            onClick={() => formikRef.current.submitForm()}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </FullScreenModal>
  );
}
